import { createContext } from "react";

export type Theme = "dark" | "light" | "system";
export type BubbleDensityMode = "auto" | "manual";

export interface RelayMetadata {
  /** List of relays with read/write permissions */
  relays: { url: string; read: boolean; write: boolean }[];
  /** Unix timestamp of when the relay list was last updated */
  updatedAt: number;
}

/** Blossom server list metadata (BUD-03 / kind 10063), mirroring RelayMetadata for parity. */
export interface BlossomServerMetadata {
  /** Ordered list of Blossom server URLs (most trusted/reliable first per BUD-03). */
  servers: string[];
  /** Unix timestamp of when the server list was last updated (from kind 10063 created_at). */
  updatedAt: number;
}

export interface AppConfig {
  /** Current theme */
  theme: Theme;
  /** NIP-65 relay list metadata */
  relayMetadata: RelayMetadata;
  /** User's kind 10063 Blossom server list. */
  blossomServerMetadata: BlossomServerMetadata;
  /**
   * When true, the app's default Blossom servers are merged with the user's
   * servers (app servers first, deduped). When false, only the user's servers
   * are used.
   */
  useAppBlossomServers: boolean;
  /** Subscribe to kind 7 reaction events for visible notes. */
  enableReactions: boolean;
  /** Show temporary background fly-bys when visible notes receive reactions. */
  spawnBubbleForReaction: boolean;
  /** When true, incoming zap comets can crack and explode regular note bubbles on impact. */
  zapBreaksBubbles: boolean;
  /** Automatically tune bubble lifetimes or use the configured target count directly. */
  bubbleDensityMode: BubbleDensityMode;
  /** Desired number of visible bubbles when density mode is manual. */
  bubbleTargetCount: number;
  /** User-entered Web of Trust root, as npub, NIP-05, or hex pubkey. */
  wotRoot: string;
  /** Resolved hex pubkey for the Web of Trust root. When set, WOT filtering is active. */
  wotRootPubkey?: string;
  /** Maximum follow distance allowed from the WOT root. */
  wotMaxDistance: number;
}

export interface AppContextType {
  /** Current application configuration */
  config: AppConfig;
  /** Update configuration using a callback that receives current config and returns new config */
  updateConfig: (updater: (currentConfig: Partial<AppConfig>) => Partial<AppConfig>) => void;
}

export const AppContext = createContext<AppContextType | undefined>(undefined);
